import React, { useEffect, useState } from 'react';
import OfferCard, { Freshness } from './OfferCard';
import DeckReshuffle from './DeckReshuffle';
import { QuickCheckAutoLoop, AddCardFlowLoop, DealHandLoop, SwipeLoop } from './AnimationLoops';
import '../../pages/CacheProject.css';

/*
 * Project-card media reel: cycles through every signature Cache component,
 * each animating through its own real states. Built for recording into an
 * mp4 for the project card (see ProjectsPage's media-section), not for live
 * embedding, since it force-mounts/unmounts scenes on an interval.
 */

const OFFERCARD_FRAMES: { freshness: Freshness; expiresIn: string; expiringSoon?: boolean; saved?: boolean }[] = [
  { freshness: 'fresh', expiresIn: '6d left' },
  { freshness: 'aging', expiresIn: '4d left' },
  { freshness: 'stale', expiresIn: '2d left' },
  { freshness: 'syncing', expiresIn: '2d left' },
  { freshness: 'fresh', expiresIn: '1d left', expiringSoon: true },
  { freshness: 'fresh', expiresIn: '1d left', expiringSoon: true, saved: true },
];

const ReelOfferCard: React.FC = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(v => (v + 1) % OFFERCARD_FRAMES.length), 700);
    return () => clearInterval(id);
  }, []);
  const f = OFFERCARD_FRAMES[i];
  return (
    <OfferCard
      merchant="Shell"
      logo="/content/cache/logos/shell.svg"
      category="gas"
      value="10% back"
      freshness={f.freshness}
      expiresIn={f.expiresIn}
      expiringSoon={f.expiringSoon}
      saved={f.saved}
    />
  );
};

type SyncState = 'synced' | 'syncing' | 'failed' | 'needs-reauth';
const SYNC_STATES: SyncState[] = ['synced', 'syncing', 'failed', 'needs-reauth'];
const SYNC_META: Record<SyncState, { label: string; icon: string }> = {
  synced: { label: 'Synced', icon: '✓' },
  syncing: { label: 'Syncing…', icon: '↻' },
  failed: { label: 'Sync failed', icon: '!' },
  'needs-reauth': { label: 'Needs reauth', icon: '⚠' },
};

const ReelSyncBadge: React.FC = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(v => (v + 1) % SYNC_STATES.length), 900);
    return () => clearInterval(id);
  }, []);
  const state = SYNC_STATES[i];
  const meta = SYNC_META[state];
  return (
    <div className={`cache__syncbadge cache__syncbadge--${state}`}>
      <span className="cache__syncbadge-dot" aria-hidden="true" />
      <span className="cache__syncbadge-icon" aria-hidden="true">{meta.icon}</span>
      <span className="cache__syncbadge-label">{meta.label}</span>
    </div>
  );
};

const SCENES: { key: string; render: () => React.ReactNode }[] = [
  { key: 'offercard', render: () => <ReelOfferCard /> },
  { key: 'syncbadge', render: () => <ReelSyncBadge /> },
  { key: 'quickcheck', render: () => <QuickCheckAutoLoop /> },
  { key: 'deck', render: () => <DeckReshuffle interval={1800} /> },
  { key: 'dealhand', render: () => <DealHandLoop /> },
  { key: 'addcard', render: () => <AddCardFlowLoop /> },
  { key: 'swipe', render: () => <SwipeLoop /> },
];

const SCENE_DURATION = 4500;

const CacheShowcaseReel: React.FC = () => {
  const [index, setIndex] = useState(0);
  const [cycle, setCycle] = useState(0);

  useEffect(() => {
    const id = setInterval(() => {
      setIndex(v => (v + 1) % SCENES.length);
      setCycle(c => c + 1);
    }, SCENE_DURATION);
    return () => clearInterval(id);
  }, []);

  const scene = SCENES[index];

  return (
    <div className="cache cache__reel">
      <div className="cache__reel-stage" key={`${scene.key}-${cycle}`}>
        {scene.render()}
      </div>
    </div>
  );
};

export default CacheShowcaseReel;
